import {
  HOSPITAL_DIRECTORY_PAGE_SIZE,
  HospitalDirectoryGroup,
  HospitalDirectoryRegion,
} from "@/features/hospital-directory/lib/directory"
import { pickLocalizedText } from "@/features/search-result/lib/pickLocalizedText"
import { SearchRequest, SearchResponse } from "@/shared/types/type/search"

// 페이지 번호만 빠진 검색 요청. 서버의 첫 페이지 fetch와 클라이언트 "더보기" fetch가
// 같은 허브를 보게 하려면 범위(regionIds·categoryIds·size)가 한 글자도 달라선 안 된다.
export type HospitalDirectorySearchScope = Omit<SearchRequest, "page">

export const buildHospitalDirectorySearchScope = ({
  region,
  group,
}: {
  region?: HospitalDirectoryRegion
  group?: HospitalDirectoryGroup
}): HospitalDirectorySearchScope => ({
  keyword: "",
  regionIds: group ? [group.regionId] : region ? [region.regionId] : [],
  categoryIds: group ? [group.treatmentId] : [],
  size: HOSPITAL_DIRECTORY_PAGE_SIZE,
})

export type HospitalDirectoryListItem = {
  hospitalOriginId: number
  // 목록 전역 순번. 화면 숫자이자 ItemList JSON-LD의 position이다.
  position: number
  name: string
  address: string
  score: number
  reviewCount: number
}

// 2페이지 첫 카드가 31이 되도록 페이지 크기를 그대로 곱한 전역 순번을 만든다.
export const hospitalDirectoryPositionOf = (page: number, index: number) =>
  (page - 1) * HOSPITAL_DIRECTORY_PAGE_SIZE + index + 1

/**
 * 응답의 `rankings[]`는 의도적으로 읽지 않는다. 검색 API는 rankingScore 순으로 정렬하는데
 * 리뷰 랭킹의 currentRank는 그 정렬과 무관해서(1페이지 실측: 134·73·101·90·186…),
 * 순위든 등락이든 이 목록에 얹으면 없는 사실이 만들어진다.
 */
export const buildHospitalDirectoryListItems = ({
  hospitals,
  page,
  locale,
}: {
  hospitals: SearchResponse[]
  page: number
  locale: string
}): HospitalDirectoryListItem[] => hospitals.map((hospital, index) => ({
  hospitalOriginId: hospital.hospitalOriginId,
  position: hospitalDirectoryPositionOf(page, index),
  name: pickLocalizedText(hospital.hospitalName, locale),
  address: hospital.jibunAddress ?? pickLocalizedText(hospital.address, locale),
  // score와 hospitalRating은 실측상 항상 같은 값이지만, 한쪽만 채워 오는 응답에 대비해
  // 기존 목록과 동일한 폴백 순서를 유지한다.
  score: Number(hospital.score ?? hospital.hospitalRating ?? 0),
  reviewCount: hospital.reviewCount ?? hospital.hospitalReviewCount ?? 0,
}))

// "서울 강남/서초" → "서울". regionName의 시도 접두 규칙은 directory.ts의
// groupHospitalDirectoryRegionsBySido와 동일하게 첫 공백 앞을 쓴다.
export const sidoNameOf = (regionName: string) =>
  regionName.trim().split(/\s+/)[0] || regionName

/**
 * 본문 첫 문단. 색인 대상이 루트 1 + 지역 123 + 그룹 57 = 181개라, 문구가 고정이면
 * 페이지마다 유니크한 본문이 h1 하나로 줄어든다. 지역·진료과·건수를 실제로 치환해
 * 페이지마다 다른 문장이 되게 한다. 화면에 없는 정보(운영시간 등)는 약속하지 않는다.
 */
export const buildHospitalDirectorySubcopy = ({
  region,
  group,
  totalHits,
}: {
  region?: HospitalDirectoryRegion
  group?: HospitalDirectoryGroup
  totalHits: number
}) => {
  const count = totalHits.toLocaleString("ko-KR")
  // 1곳뿐인 허브에서 "비교해 보세요"는 비교 대상이 없다.
  const single = totalHits <= 1
  if (group) {
    return single
      ? `${group.regionName}의 ${group.treatmentName} 병원 ${count}곳입니다. 실제 이용자 리뷰와 위치를 확인해 보세요.`
      : `${group.regionName} ${group.treatmentName} 병원 ${count}곳을 실제 이용자 리뷰와 위치로 비교해 보세요.`
  }
  if (region) {
    return single
      ? `${region.regionName}의 병원 ${count}곳입니다. 진료과와 실제 이용자 리뷰를 확인해 보세요.`
      : `${region.regionName} 병원 ${count}곳을 진료과와 실제 이용자 리뷰로 비교해 보세요.`
  }
  return single
    ? `리뷰드에 등록된 병원 ${count}곳입니다. 실제 이용자 리뷰를 확인해 보세요.`
    : `전국 병원 ${count}곳을 지역과 진료과로 좁혀 실제 이용자 리뷰와 함께 찾아보세요.`
}
